import React, { useEffect, useRef, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { FiMenu, FiX } from "react-icons/fi";
import { FaUserCircle, FaAngleDown, FaSignOutAlt } from "react-icons/fa";
import styles from "./Navbar.module.css";
import { BRAND } from "../data/content";
import { useAuth } from "../context/AuthContext";

const NAV_LINKS = [
  { to: "/",          label: "Home" },
  { to: "/services",  label: "Services" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/pricing",   label: "Pricing" },
  { to: "/about",     label: "About" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef(null);
  const location = useLocation();
  const { currentUser, isAdmin, logout, userData } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Close dropdown on route change
  useEffect(() => { setUserDropdownOpen(false); }, [location.pathname]);

  // Close user dropdown when clicking outside
  useEffect(() => {
    const onPointerDown = (event) => {
      if (!userDropdownRef.current) return;
      if (!userDropdownRef.current.contains(event.target)) {
        setUserDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, []);

  // Lock scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const onLogout = async () => {
    await logout();
  };

  return (
    <header className={`${styles.navbar} ${scrolled ? styles.scrolled : ""}`}>
      <div className={styles.inner}>
        <Link to="/" className={styles.brand} aria-label={BRAND.name}>
          <img src="/enndo_icon.png" alt="EnndoWeb Solution" className={styles.brandLogo} />
          <span className={styles.brandText}>
            Enndo<span> Web Solution</span>
          </span>
        </Link>

        <nav className={styles.links}>
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `${styles.link} ${isActive ? styles.active : ""}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className={styles.actions}>
          <Link to="/contact" className={styles.cta}>
            Get a Free Quote →
          </Link>

          {!currentUser ? (
            <Link to="/auth" className={styles.secondaryBtn}>Sign in</Link>
          ) : (
            <div className={styles.userDropdownContainer} ref={userDropdownRef}>
              <button
                className={styles.userToggleBtn}
                onClick={() => setUserDropdownOpen((v) => !v)}
                aria-label="User menu"
              >
                <FaUserCircle className={styles.userIcon} />
                <span className={styles.userName}>
                  {(userData?.name || currentUser?.displayName || currentUser?.email || "User").split(" ")[0]}
                </span>
                <FaAngleDown className={`${styles.angleIcon} ${userDropdownOpen ? styles.open : ""}`} />
              </button>
              {userDropdownOpen && (
                <div className={styles.userDropdownMenu}>
                  <div className={styles.userHeader}>
                    <div className={styles.userAvatar}>
                      {userData?.photoURL ? (
                        <img src={userData.photoURL} alt={userData?.name || 'User'} />
                      ) : (
                        <FaUserCircle className={styles.userAvatarIcon} />
                      )}
                    </div>
                    <div className={styles.userHeaderInfo}>
                      <div className={styles.userHeaderName}>{userData?.name || currentUser?.displayName || 'User'}</div>
                      <div className={styles.userHeaderEmail}>{currentUser?.email}</div>
                    </div>
                  </div>
                  <div className={styles.menuDivider} />
                  <Link to="/profile" className={styles.menuItem} onClick={() => setUserDropdownOpen(false)}>
                    My Profile
                  </Link>
                  {isAdmin && (
                    <Link to="/admin" className={styles.menuItem} onClick={() => setUserDropdownOpen(false)}>
                      Admin Dashboard
                    </Link>
                  )}
                  <div className={styles.menuDivider} />
                  <button
                    className={styles.logoutItem}
                    onClick={() => {
                      setUserDropdownOpen(false);
                      onLogout();
                    }}
                  >
                    <FaSignOutAlt />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <button
          className={styles.menuBtn}
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <FiX size={22} /> : <FiMenu size={22} />}
        </button>
      </div>

      <div className={`${styles.mobileMenu} ${open ? styles.open : ""}`}>
        {NAV_LINKS.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.to === "/"}
            className={({ isActive }) =>
              `${styles.mobileLink} ${isActive ? styles.active : ""}`
            }
          >
            {l.label}
          </NavLink>
        ))}
        <Link to="/contact" className={styles.mobileCta}>
          Get a Free Quote →
        </Link>
        {!currentUser ? (
          <Link to="/auth" className={styles.mobileLink}>Sign in</Link>
        ) : (
          <>
            <div className={styles.mobileUserInfo}>
              <div>{userData?.name || currentUser?.displayName || 'User'}</div>
              <div className={styles.mobileUserEmail}>{currentUser?.email}</div>
            </div>
            <Link to="/profile" className={styles.mobileLink}>My Profile</Link>
            {isAdmin && <Link to="/admin" className={styles.mobileLink}>Admin Dashboard</Link>}
            <button className={styles.mobileLink} onClick={onLogout}>Logout</button>
          </>
        )}
      </div>
    </header>
  );
}
